import { useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";

import { useGetRecordingCamerasQuery } from "@/shared/api";
import {
  CameraMode,
  selectCurrentCamera,
  selectGondolas,
  selectSelectedCameraMode,
  selectSelectedGondola,
  selectSelectedOrder,
  setSelectedCameraMode,
} from "@/entities/annotation";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { CAMERA_MODES } from "@/entities/annotation/model/consts";

export function useCameras() {
  const { storeKey = "" } = useParams();
  const dispatch = useAppDispatch();
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const selectedGondola = useAppSelector(selectSelectedGondola);
  const selectedCamera = useAppSelector(selectCurrentCamera);
  const selectedCameraMode = useAppSelector(selectSelectedCameraMode);
  const activeCamera = selectedCamera;
  const gondolas = useAppSelector(selectGondolas);

  const gondolaName = selectedGondola?.name;

  const shelfTrackingCameras = selectedGondola?.shelfTrackingCameras;

  const handTrackingCameras = selectedGondola?.handTrackingCameras;

  const { data: allCameras } = useGetRecordingCamerasQuery(
    { atTime: selectedOrder?.time, storeKey },
    {
      skip: !selectedOrder || !selectedOrder?.time,
    }
  );

  const sortedCameras = useMemo(
    () => allCameras?.slice().sort((a, b) => a - b) ?? [],
    [allCameras]
  );

  const availableCameras = useMemo(
    () => new Set(sortedCameras.map(String)),
    [sortedCameras]
  );

  const findCameraGondolaName = useCallback(
    (camera: string | number | null): string | null => {
      const cameraId = String(camera);

      if (!cameraId || !availableCameras.has(cameraId)) return null;

      const gondola = gondolas.find((gondola) =>
        gondola.cameras.some((camera) => camera.id === cameraId)
      );

      return gondola?.name ?? null;
    },
    [availableCameras, gondolas]
  );

  const findCameraMode = useCallback(
    (camera: string | number): CameraMode => {
      const cameraId = String(camera);

      const gondola = gondolas.find((gondola) =>
        gondola.cameras.some((camera) => camera.id === cameraId)
      );

      if (!gondola) return CAMERA_MODES.ALL;

      const gondolaCamera = gondola.cameras.find(
        (camera) => camera.id === cameraId
      );

      if (gondolaCamera?.cameraType === "hand_tracking") {
        return CAMERA_MODES.HAND;
      }

      if (gondolaCamera?.cameraType === "shelf_tracking") {
        return CAMERA_MODES.SHELF;
      }

      return CAMERA_MODES.ALL;
    },
    [gondolas]
  );

  const setCameraModeFromCameraId = useCallback(
    (cameraId: string | number) => {
      const mode = findCameraMode(cameraId);
      if (
        selectedCameraMode !== CAMERA_MODES.ALL &&
        selectedCameraMode !== mode
      ) {
        dispatch(setSelectedCameraMode(CAMERA_MODES.ALL));
      }
    },
    [findCameraMode, dispatch, selectedCameraMode]
  );

  const organized = useMemo(() => {
    const filteredShelfTrackingCameras = shelfTrackingCameras?.filter(
      (camera) => camera != null && availableCameras.has(camera.id)
    );

    const filteredHandTrackingCameras = handTrackingCameras?.filter(
      (camera) => camera != null && availableCameras.has(camera.id)
    );

    const gondolaCameras = gondolaName
      ? {
          handTracking: filteredHandTrackingCameras,
          shelfTracking: filteredShelfTrackingCameras,
        }
      : {
          handTracking: [],
          shelfTracking: [],
        };

    return {
      selectedGondola: activeCamera
        ? findCameraGondolaName(activeCamera)
        : null,
      gondolaCameras,
      findCameraGondolaName,
      findCameraMode,
      setCameraModeFromCameraId,
      allCameras: sortedCameras.map((cameraId) => ({
        id: cameraId.toString(),
      })),
    };
  }, [
    sortedCameras,
    availableCameras,
    shelfTrackingCameras,
    activeCamera,
    gondolaName,
    findCameraGondolaName,
    findCameraMode,
    setCameraModeFromCameraId,
    handTrackingCameras,
  ]);

  return organized;
}
